import HelpButton from "@/components/HelpButton/HelpButton";
import NoImpactBanner from "@/components/NoImpactBanner/NoImpactBanner";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import { useCompanyAccess } from "@/hooks/useCompanySettings";
import styles from "@/pages/dashboard/Dashboard.module.scss";
import { Button } from "antd";
import { Link, useNavigate } from "react-router-dom";
import MetricSelector from "../components/MetricSelector/MetricSelector";
import YearPicker from "../components/YearPicker/YearPicker";
import { useDashboardYear } from "../hooks/useDateRange";
import useImpactMetric from "../hooks/useImpactMetric";
import { useMaterialImpactTotals } from "../hooks/useMaterialImpact";
import { useDashboardOrderCounts } from "../hooks/usePoSo";
import MaterialImpactPerMaterial from "./widgets/MaterialImpactPerMaterial";
import MaterialImpactPerScope from "./widgets/MaterialImpactPerScope";
import MaterialImpactTopContributors from "./widgets/MaterialImpactTopContributors";
import MaterialImpactTotal from "./widgets/MaterialImpactTotal";
import PremiumExplore from "@/components/PremiumFeatureComponents/PremiumExplore";

export default function MaterialDashboard() {
  const metric = useImpactMetric();
  const navigate = useNavigate();
  const access = useCompanyAccess();

  const { data: orderCounts } = useDashboardOrderCounts({});
  const { data: totalImpact } = useMaterialImpactTotals({ metrics: [metric.key] });
  const { year } = useDashboardYear();
  const pageTitle = `Material impact`;
  const pageDescription =
    "Insight into the part of your total company impact attributed to materials.";

  if (access && !access.includes("lca"))
    return (
      <PremiumExplore
        title={pageTitle}
        description={pageDescription}
      />
    );

  return (
    <div>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        right={
          <>
            <YearPicker />
            <HelpButton url="https://help.root-sustainability.com/en/articles/9899034-material-impact-dashboard" />
          </>
        }
      />
      <MetricSelector
        value={metric.key}
        onChange={(key) => navigate("/dashboard/materials/" + key)}
      />
      {orderCounts?.po === 0 ? (
        <NoImpactBanner title={`No material impact for ${year}`}>
          <span>
            Start with uploading purchase orders for {year}, so we can calculate your material
            impact.
          </span>
          <Link to="/orders/purchase_orders">
            <Button type="primary">Upload orders</Button>
          </Link>
        </NoImpactBanner>
      ) : totalImpact?.material_impact[metric.key] === 0 ? (
        <NoImpactBanner title={`No material impact for ${year}`}>
          <span>What to do?</span>
          <ul>
            <li>
              Add the bill of materials for the related <Link to="/products">products</Link>
            </li>
            <li>
              Match the <Link to="/materials">materials</Link> with ther impact
            </li>
          </ul>
        </NoImpactBanner>
      ) : (
        <div className={styles.grid}>
          <div className={styles["two-columns"]}>
            <div>
              <MaterialImpactTotal metric={metric} />
              {metric.key === "carbon_footprint" && <MaterialImpactPerScope />}
            </div>
            <div>
              <MaterialImpactTopContributors metric={metric} />
            </div>
          </div>
          <div className={styles["one-column"]}>
            <MaterialImpactPerMaterial metric={metric} />
          </div>
        </div>
      )}
    </div>
  );
}
